import { type JSX } from "react";
import type { components } from "../../api/schema";

type WorldlineNode = components["schemas"]["WorldlineNode"];

export type WorldlineTreeNodeViewProps = {
  node: WorldlineNode;
  /** 現在プレイ中の世界線かどうか。true のときハイライト表示する */
  isHighlighted: boolean;
  scenarioId: string;
  /** ツリー内の深さ（ルート = 0）。CSS インデント量の計算に使用する */
  depth: number;
  /**
   * 「ここから分岐」ボタンを押したときに呼ばれるコールバック。
   * fork に必要な branchFromSceneId/branchFromChoiceId は
   * 親コンポーネント（WorldlineTreeView）側で BackscrollView を開いて選択させるため、
   * ここでは worldlineId のみを渡す。
   */
  onForkRequest?: ((worldlineId: string) => void) | undefined;
};

/** インデント 1 レベルあたりのピクセル数 */
const INDENT_PX = 24;

/**
 * 世界線ツリーの 1 ノード分を描画するコンポーネント。
 *
 * - スキーマの WorldlineNode 型との命名衝突を避けるため WorldlineTreeNodeView と命名する。
 * - 「ここから分岐」ボタンを押すと onForkRequest に worldlineId を渡す。
 *   実際の fork API 呼び出しは親コンポーネントが担当し、このコンポーネントは UI のみに専念する。
 */
export function WorldlineTreeNodeView({
  node,
  isHighlighted,
  depth,
  onForkRequest,
}: WorldlineTreeNodeViewProps): JSX.Element {
  const shortId = node.id.slice(0, 8);
  const indentPx = depth * INDENT_PX;

  return (
    <div
      data-testid={`worldline-node-${node.id}`}
      style={{ paddingLeft: `${indentPx}px` }}
      className={[
        "flex items-center gap-2 py-1 px-2 rounded",
        isHighlighted
          ? "font-bold ring-1 ring-indigo-400 bg-indigo-900/40 text-indigo-100"
          : "text-slate-300",
      ].join(" ")}
    >
      {/* ID（先頭8文字）を表示する */}
      <span className="font-mono text-xs">{shortId}</span>

      {/* 終了した世界線にバッジを表示する */}
      {node.isEnded && (
        <span
          data-testid="worldline-node-ended-badge"
          className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-400"
        >
          終了
        </span>
      )}

      {/* 「ここから分岐」ボタン */}
      <button
        type="button"
        data-testid={`fork-from-worldline-${node.id}`}
        onClick={() => onForkRequest?.(node.id)}
        className="ml-auto text-xs px-2 py-0.5 rounded border border-indigo-700/50 bg-indigo-900/40 hover:bg-indigo-800 text-indigo-300 hover:text-indigo-100"
      >
        ここから分岐
      </button>
    </div>
  );
}
